import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LucideAngularModule } from 'lucide-angular';
import { PageHeaderComponent } from 'src/app/shared/components/page-header/page-header.component';
import { UpdateNoteService } from '../../services/update-note.service';
import { UPDATE_NOTE_TYPES, UpdateNoteType } from '../../types/update-note';
import { UpdateNoteCardComponent } from '../update-note-card/update-note-card.component';

@Component({
  selector: 'app-update-notes-page',
  imports: [
    FormsModule,
    TranslocoPipe,
    ButtonModule,
    SelectButtonModule,
    LucideAngularModule,
    PageHeaderComponent,
    UpdateNoteCardComponent,
  ],
  templateUrl: './update-notes-page.component.html',
})
export class UpdateNotesPageComponent implements OnInit {
  private updateNoteService = inject(UpdateNoteService);
  private translocoService = inject(TranslocoService);

  notes = this.updateNoteService.notes;
  isLoading = this.updateNoteService.isLoading;
  error = this.updateNoteService.error;

  /** null = no filter, show everything. */
  selectedType = signal<UpdateNoteType | null>(null);

  private activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  /** Rebuilt on language change: the labels are baked in via optionLabel. */
  typeOptions = computed<{ label: string; value: UpdateNoteType | null }[]>(
    () => {
      this.activeLang();
      return [
        { label: this.translocoService.translate('updateNotes.filter.all'), value: null },
        ...UPDATE_NOTE_TYPES.map((type) => ({
          label: this.translocoService.translate(`updateNotes.filter.${type}`),
          value: type as UpdateNoteType | null,
        })),
      ];
    },
  );

  filteredNotes = computed(() => {
    const type = this.selectedType();
    const notes = this.notes();
    return type === null ? notes : notes.filter((note) => note.type === type);
  });

  ngOnInit(): void {
    this.updateNoteService.loadIfNeeded();
  }

  clearFilter(): void {
    this.selectedType.set(null);
  }

  retry(): void {
    this.updateNoteService.reload();
  }
}
